<template>
  <div class="contact-conversation--panel">
    <div v-if="!uiFlags.isFetching" class="contact-conversation__wrap">
      <div v-if="!mappedWiseConversations.length" class="no-label-message">
        <span>
          {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
        </span>
      </div>
      <div v-else class="contact-conversation--list">
        <conversation-card
          v-for="conversation in mappedWiseConversations"
          :key="conversation.id"
          :chat="conversation"
          :hide-inbox-name="false"
          :hide-thumbnail="true"
          class="compact"
          @assign-agent="onAssignAgent"
          @assign-team="onAssignTeam"
          @assign-label="onAssignLabels"
          @update-conversation-status="toggleConversationStatus"
          @open-modal="openSnoozeModal"
          @mark-as-unread="markAsUnread"
          @assign-priority="assignPriority"
        />
      </div>
    </div>
    <spinner v-else />
    <woot-snoozed-modal
      v-if="showSnoozedModal"
      :show="showSnoozedModal"
      :scheduled-at="scheduledAt"
      @close="closeModal"
    />
  </div>
</template>

<script>
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard';
import Spinner from 'shared/components/Spinner';
import * as WiseAPI from 'dashboard/api/wise';
import alertMixin from 'shared/mixins/alertMixin';
import WootSnoozedModal from 'dashboard/components/widgets/conversation/WootSnoozedModal.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    ConversationCard,
    Spinner,
    WootSnoozedModal,
  },
  mixins: [alertMixin],
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
    }),
    mappedWiseConversations() {
      return this.wiseConversations.map(conversation => {
        const live = this.$store.getters.getConversationById(conversation.id);
        return live ? { ...conversation, priority: live.priority } : conversation;
      });
    },
  },
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    accountId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      wiseConversations: [],
      uiFlags: { isFetching: false },
      showSnoozedModal: false,
      scheduledAt: null,
    };
  },
  watch: {
    contactId() {
      this.fetchWiseConversations();
    },
  },
  mounted() {
    this.fetchWiseConversations();
  },
  methods: {
    async onAssignAgent(agent, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId,
          fields: { assignee_id: agent.id },
        });
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
            agentName: agent.name,
            conversationId,
          })
        );
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.ASSIGN_FAILED'));
      }
    },
    async onAssignTeam(team, conversationId = null) {
      try {
        await this.$store.dispatch('assignTeam', { conversationId, teamId: team.id });
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
            team: team.name,
            conversationId,
          })
        );
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
      }
    },
    async onAssignLabels(labels, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId,
          labels: { add: labels },
        });
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL', {
            labelName: labels[0],
            conversationId,
          })
        );
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
    },
    toggleConversationStatus(conversationId, status, snoozedUntil) {
      const conversation = this.$store.getters.getConversationById(conversationId);
      const contactId = conversation?.meta?.sender?.id || conversation?.contact_id;
      const isLimitedAgent =
        !this.currentUser?.permissions?.see_conversations_team &&
        !this.currentUser?.permissions?.see_all_conversations &&
        this.currentUser?.type_role !== 'administrator';
      const isPermissionSeeAllTeamConversation =
        this.currentUser?.type_role === 'administrator' ||
        this.currentUser?.permissions?.see_all_conversations ||
        this.currentUser?.permissions?.see_conversations_team;
      const contactConversations = this.$store.getters['contactConversations/getContactConversation'](
        contactId,
        this.currentUser?.id,
        isLimitedAgent,
        !isPermissionSeeAllTeamConversation
      );
      const validateStatus = {
        activeInbox: conversation?.inbox_id,
        contactConversations,
        currentId: this.currentUser?.id,
        contactId,
        messageSaved: this.$t('CONVERSATION.CHANGE_STATUS'),
        messageOpen: this.$t('CONTACT_PANEL.ALREADY_OPEN_CONVERSATION'),
        conversation,
      };
      this.$store.dispatch('toggleStatus', { conversationId, status, snoozedUntil, validateStatus });
    },
    openSnoozeModal(value) {
      this.showSnoozedModal = value;
    },
    closeModal() {
      this.showSnoozedModal = false;
    },
    async markAsUnread(conversationId) {
      try {
        await this.$store.dispatch('markMessagesUnread', { id: conversationId });
      } catch (error) {
        // Ignore error
      }
    },
    async assignPriority(priority, conversationId = null) {
      this.$store.dispatch('setCurrentChatPriority', { priority, conversationId });
      this.$store.dispatch('assignPriority', { conversationId, priority }).then(() => {
        this.showAlert(
          this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', { priority, conversationId })
        );
      });
    },
    async fetchWiseConversations() {
      this.uiFlags.isFetching = true;
      try {
        const { data } = await WiseAPI.getContactWiseConversations(this.accountId, this.contactId);
        this.wiseConversations = data.map(conversation => ({
          ...conversation,
          contact: { id: this.contactId },
        }));
      } catch (e) {
        this.wiseConversations = [];
      } finally {
        this.uiFlags.isFetching = false;
      }
    },
  },
};
</script>

<style scoped>
.contact-conversation--panel {
  padding: 0.5rem 0;
}
.contact-conversation__wrap {
  min-height: 60px;
}
.no-label-message {
  color: #888;
  text-align: center;
  padding: 1rem 0;
}
</style>
